import { useCallback, useState } from "react"
import {
  Anchor,
  Archive,
  Check,
  CheckCircle2,
  Copy,
  Download,
  Eye,
  FileText,
  Loader2,
  Package,
  Send,
  Share2,
  Ship,
  Undo2,
  Weight,
  X,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { DocumentsSidePanel } from "@/features/transit/components/documents-side-panel"
import {
  useArchiveOT,
  useUnarchiveOT,
} from "@/features/transit/hooks/use-transit-mutations"
import {
  useCreateTraceabilityShare,
  useResendTraceabilityShare,
  useRevokeTraceabilityShare,
} from "@/features/transit/hooks/use-doc-mutations"
import {
  useTransitShares,
  useTransitTraceability,
} from "@/features/transit/hooks/use-transit-orders"
import { resolveApiError } from "@/lib/api/error-i18n"
import { totalDocProgress } from "@/features/transit/lib/doc-slots"
import { lotsTotalBags, lotsTotalMT } from "@/features/transit/lib/ot-math"
import { exportTraceabilityBundle } from "@/features/transit/lib/traceability-bundle"
import { paths } from "@/routes/paths"
import type {
  OT,
  OTShareSummary,
  OTStage,
} from "@/features/transit/api/types"

interface TerminerPanelProps {
  ot: OT
}

export function TerminerPanel({ ot }: TerminerPanelProps) {
  return (
    <div className="flex flex-col gap-3">
      <CompletionHero ot={ot} />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <DocumentsCard ot={ot} />
        <ShareTraceabilityCard ot={ot} />
      </div>
    </div>
  )
}

// ── Completion hero ────────────────────────────────────────────────

function CompletionHero({ ot }: { ot: OT }) {
  const { t, i18n } = useTranslation("transit")
  const archive = useArchiveOT(ot.id)
  const unarchive = useUnarchiveOT(ot.id)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const completedAt = formatCompletedAt(ot.completedAt, i18n.language)

  const lotCount = ot.lots?.length ?? 0
  const totalMT = lotsTotalMT(ot)
  const totalBags = lotsTotalBags(ot)
  const vesselText = [ot.vessel, ot.voyage].filter(Boolean).join(" · ")

  // Card primitive defaults to `flex flex-col gap-6 py-6`; we collapse
  // gap + padding so the two internal rows sit close together.
  return (
    <Card
      className="!gap-0 border-emerald-200 !py-0 dark:border-emerald-500/30"
      style={{
        backgroundImage:
          "radial-gradient(rgba(16,185,129,0.08) 1px, transparent 1px)",
        backgroundSize: "12px 12px",
      }}
    >
      <div className="flex items-center gap-3 px-5 py-3.5">
        <div className="flex size-9 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm">
          <CheckCircle2 className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold tracking-tight text-foreground">
            {t("terminer.title")}
          </h2>
          {completedAt ? (
            <p className="text-xs text-muted-foreground">
              {t("terminer.completedOn", { at: completedAt })}
            </p>
          ) : null}
        </div>
        {!ot.archived ? (
          <Button
            size="sm"
            onClick={() => setConfirmOpen(true)}
            disabled={archive.isPending}
            className="flex-shrink-0"
          >
            <Archive className="size-4" />
            {t("terminer.archiveBtn")}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => unarchive.mutate()}
            disabled={unarchive.isPending}
            className="flex-shrink-0"
          >
            <Undo2 className="size-4" />
            {t("terminer.unarchiveBtn")}
          </Button>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("terminer.archiveConfirmTitle")}</DialogTitle>
            <DialogDescription>
              {t("terminer.archiveConfirmSubtitle")}
            </DialogDescription>
          </DialogHeader>
          <ul className="text-foreground/80 space-y-1.5 text-xs">
            <li className="flex items-start gap-2">
              <span aria-hidden className="text-amber-600">
                •
              </span>
              <span>{t("terminer.archiveConfirmPoint1")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span aria-hidden className="text-amber-600">
                •
              </span>
              <span>{t("terminer.archiveConfirmPoint2")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span aria-hidden className="text-emerald-600">
                ✓
              </span>
              <span>{t("terminer.archiveConfirmPoint3")}</span>
            </li>
          </ul>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={archive.isPending}
            >
              {t("wizard.cancel")}
            </Button>
            <Button
              onClick={() => {
                archive.mutate(undefined, {
                  onSuccess: () => setConfirmOpen(false),
                })
              }}
              disabled={archive.isPending}
            >
              <Archive className="size-4" />
              {t("terminer.archiveConfirmBtn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2 divide-x divide-y border-t sm:grid-cols-4 sm:divide-y-0">
        <SummaryCell
          icon={Anchor}
          label={t("terminer.summary.client")}
          value={ot.clientName}
        />
        <SummaryCell
          icon={Package}
          label={t("terminer.summary.contract")}
          value={ot.contractNumber ?? `#${ot.contractId}`}
        />
        <SummaryCell
          icon={Ship}
          label={t("terminer.summary.vessel")}
          value={vesselText || "—"}
        />
        <SummaryCell
          icon={Weight}
          label={t("terminer.summary.shipped")}
          value={t("terminer.summary.shippedValue", {
            lots: lotCount,
            mt: totalMT.toFixed(3),
            bags: totalBags.toLocaleString(),
          })}
        />
      </div>
    </Card>
  )
}

// Summary cell — used inside the hero card's bottom row.
function SummaryCell({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
}) {
  return (
    <div className="flex items-center gap-2.5 px-4 py-3">
      <Icon className="size-4 flex-shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
          {label}
        </div>
        <div className="truncate text-sm font-medium text-foreground">
          {value}
        </div>
      </div>
    </div>
  )
}

// ── Documents summary ─────────────────────────────────────────────

function DocumentsCard({ ot }: { ot: OT }) {
  const { t } = useTranslation("transit")
  const [open, setOpen] = useState(false)
  const totals = totalDocProgress(ot)
  const photoCount = ot.photos?.length ?? 0
  const redevanceCount = ot.redevances?.length ?? 0
  const empotageCount = ot.empotageFiles?.length ?? 0
  const draftBLCount = ot.draftBLs?.length ?? 0
  const cccCount = (ot.lots ?? []).reduce(
    (acc, l) => acc + (l.cccDocs?.length ?? 0),
    0
  )

  return (
    <>
      <Card className="flex h-full flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex size-9 flex-shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
              <FileText className="size-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">
                {t("terminer.docs.title")}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("terminer.docs.summary", {
                  filled: totals.filled,
                  total: totals.total,
                })}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            {t("terminer.docs.viewAll")}
          </Button>
        </div>

        <ul className="grid flex-1 grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground tabular-nums content-start">
          <DocLine label={t("terminer.docs.slotDocs")} value={totals.filled} />
          <DocLine label={t("terminer.docs.photos")} value={photoCount} />
          <DocLine
            label={t("terminer.docs.empotageFiles")}
            value={empotageCount}
          />
          <DocLine
            label={t("terminer.docs.redevances")}
            value={redevanceCount}
          />
          <DocLine label={t("terminer.docs.draftBL")} value={draftBLCount} />
          <DocLine label={t("terminer.docs.ccc")} value={cccCount} />
        </ul>
      </Card>

      <DocumentsSidePanel
        ot={ot}
        triggerless
        open={open}
        onOpenChange={setOpen}
      />
    </>
  )
}

function DocLine({ label, value }: { label: string; value: number }) {
  return (
    <li className="flex items-center justify-between gap-2">
      <span className="truncate">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </li>
  )
}

function ShareBullet({ label }: { label: string }) {
  return (
    <li className="flex items-center gap-2">
      <span aria-hidden className="text-emerald-600">
        ✓
      </span>
      <span className="truncate">{label}</span>
    </li>
  )
}

// ── Share traceability ────────────────────────────────────────────

function ShareTraceabilityCard({ ot }: { ot: OT }) {
  const { t } = useTranslation("transit")
  const create = useCreateTraceabilityShare(ot.id)
  const revoke = useRevokeTraceabilityShare(ot.id)
  const resend = useResendTraceabilityShare(ot.id)
  const sharesQuery = useTransitShares(ot.id)
  const traceability = useTransitTraceability(ot.id, { enabled: false })

  const [open, setOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [to, setTo] = useState("")
  const [by, setBy] = useState("")
  // Last successful share. Surface a "sent" panel with a copy-to-clipboard
  // button right after creation so the operator can also paste the link
  // into a chat / Slack message in addition to the auto-emailed one.
  const [lastShare, setLastShare] = useState<{
    url: string
    email: string
  } | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)

  const shares = sharesQuery.data ?? []
  const activeShares = shares.filter((s) => !s.revokedAt)

  const handleExport = useCallback(async () => {
    if (exporting) return
    setExporting(true)
    try {
      // Pass the cached trace if we have it; the bundle helper will
      // fetch inline when undefined, so a cold export still works.
      const result = await exportTraceabilityBundle(
        ot,
        traceability.data,
        (stage: OTStage) => t(`stages.${stage}`),
      )
      if (result.failedFiles > 0) {
        toast.warning(
          t("terminer.bundlePartial", {
            failed: result.failedFiles,
            total: result.totalFiles,
          }),
        )
      } else {
        toast.success(t("terminer.bundleReady"))
      }
    } catch (error) {
      toast.error(resolveApiError(error, t))
    } finally {
      setExporting(false)
    }
  }, [exporting, ot, t, traceability])

  const handleCreate = () => {
    const recipient = to.trim()
    const sender = by.trim()
    if (!recipient || !sender) return
    create.mutate(
      { to: recipient, by: sender },
      {
        onSuccess: (data) => {
          setOpen(false)
          setTo("")
          setBy("")
          setLastShare({ url: data.shareUrl, email: data.share.recipientEmail })
          toast.success(t("terminer.shareSentToast", { email: recipient }))
        },
      },
    )
  }

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setLinkCopied(true)
      window.setTimeout(() => setLinkCopied(false), 2000)
    } catch {
      toast.error(t("terminer.shareCopyError"))
    }
  }

  return (
    <Card className="flex h-full flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex size-9 flex-shrink-0 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
            <Share2 className="size-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-foreground">
              {t("terminer.shareTitle")}
            </div>
            <p className="text-xs text-muted-foreground">
              {activeShares.length > 0
                ? t("terminer.shareSummaryActive", {
                    count: activeShares.length,
                  })
                : t("terminer.shareSummary")}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <Share2 className="size-4" />
          {activeShares.length > 0
            ? t("terminer.shareNewBtn")
            : t("terminer.shareBtn")}
        </Button>
      </div>

      {lastShare ? (
        <div className="rounded-md border border-emerald-200/70 bg-emerald-50/50 p-3 text-xs dark:border-emerald-500/30 dark:bg-emerald-500/10">
          <div className="text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold uppercase tracking-wide">
            {t("terminer.shareSentTo")} {lastShare.email}
          </div>
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-background/60 px-2 py-1 font-mono text-[11px]">
              {lastShare.url}
            </code>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => handleCopyLink(lastShare.url)}
              aria-label={t("terminer.shareCopyLink")}
            >
              {linkCopied ? (
                <Check className="size-3.5" />
              ) : (
                <Copy className="size-3.5" />
              )}
            </Button>
          </div>
          <p className="mt-2 text-muted-foreground">
            {t("terminer.shareSentNote")}
          </p>
        </div>
      ) : (
        <ul className="text-muted-foreground grid grid-cols-1 gap-x-3 gap-y-1 text-[11px]">
          <ShareBullet label={t("terminer.shareIncludes.lots")} />
          <ShareBullet label={t("terminer.shareIncludes.docs")} />
          <ShareBullet label={t("terminer.shareIncludes.photos")} />
        </ul>
      )}

      {shares.length > 0 ? (
        <div className="rounded-md border bg-card">
          <div className="border-b px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t("terminer.shareHistoryTitle")}
          </div>
          <ul className="divide-y text-xs">
            {shares.map((share) => (
              <ShareHistoryRow
                key={share.id}
                share={share}
                onRevoke={() => revoke.mutate(share.id)}
                onResend={() =>
                  resend.mutate(share.id, {
                    onSuccess: () =>
                      toast.success(
                        t("terminer.shareResentToast", {
                          email: share.recipientEmail,
                        }),
                      ),
                  })
                }
                isRevoking={revoke.isPending}
                isResending={resend.isPending}
              />
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-auto flex items-center gap-1 pt-1 text-xs">
        <Link
          to={paths.transitOrderTraceability(ot.id)}
          className="inline-flex items-center gap-1.5 rounded text-muted-foreground transition-colors hover:text-foreground hover:underline"
        >
          <Eye className="size-3.5" />
          {t("terminer.viewDataBtn")}
        </Link>
        <span className="text-muted-foreground/60" aria-hidden>
          ·
        </span>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-1.5 rounded text-muted-foreground transition-colors hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-60"
        >
          {exporting ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Download className="size-3.5" />
          )}
          {exporting
            ? t("terminer.bundlePending")
            : t("terminer.downloadDataBtn")}
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("terminer.shareModalTitle")}</DialogTitle>
            <DialogDescription>
              {t("terminer.shareModalSubtitle")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>{t("terminer.recipientLabel")}</Label>
              <Input
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder={t("terminer.recipientPlaceholder")}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t("terminer.byLabel")}</Label>
              <Input
                value={by}
                onChange={(e) => setBy(e.target.value)}
                placeholder={t("terminer.byPlaceholder")}
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              {t("terminer.sharePinNote")}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t("wizard.cancel")}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!to.trim() || !by.trim() || create.isPending}
            >
              {create.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Share2 className="size-4" />
              )}
              {t("terminer.shareBtn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function ShareHistoryRow({
  share,
  onRevoke,
  onResend,
  isRevoking,
  isResending,
}: {
  share: OTShareSummary
  onRevoke: () => void
  onResend: () => void
  isRevoking: boolean
  isResending: boolean
}) {
  const { t, i18n } = useTranslation("transit")
  const expired = isExpired(share.expiresAt)
  const revoked = !!share.revokedAt
  const status = revoked
    ? "revoked"
    : expired
      ? "expired"
      : "active"
  return (
    <li className="flex items-start gap-2 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-foreground">
            {share.recipientEmail}
          </span>
          <ShareStatusChip status={status} />
        </div>
        <div className="text-[10px] text-muted-foreground">
          {t("terminer.shareSentAt", {
            at: formatShareDate(share.createdAt, i18n.language),
          })}
          {share.viewCount > 0
            ? ` · ${t("terminer.shareViewedTimes", { count: share.viewCount })}`
            : null}
          {share.sharedBy ? ` · ${share.sharedBy}` : null}
        </div>
      </div>
      {!revoked && !expired ? (
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onResend}
            disabled={isResending}
            aria-label={t("terminer.shareResendBtn")}
          >
            {isResending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Send className="size-3.5" />
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onRevoke}
            disabled={isRevoking}
            aria-label={t("terminer.shareRevokeBtn")}
          >
            {isRevoking ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <X className="size-3.5" />
            )}
          </Button>
        </div>
      ) : null}
    </li>
  )
}

function ShareStatusChip({
  status,
}: {
  status: "active" | "expired" | "revoked"
}) {
  const { t } = useTranslation("transit")
  const palette = {
    active:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    expired: "bg-muted text-muted-foreground",
    revoked: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
  } as const
  return (
    <span
      className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${palette[status]}`}
    >
      {t(`terminer.shareStatus.${status}`)}
    </span>
  )
}

function isExpired(expiresAt: string): boolean {
  const t = Date.parse(expiresAt)
  if (Number.isNaN(t)) return false
  return t < Date.now()
}

function formatShareDate(value: string, locale: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  try {
    return d.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  } catch {
    return value.slice(0, 10)
  }
}

// Render an ISO timestamp as the user's locale (e.g. "29 avril 2026 à
// 11:16"). Returns null when the timestamp is missing/invalid.
function formatCompletedAt(value: string | null | undefined, locale: string) {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  try {
    return d.toLocaleString(locale, {
      dateStyle: "long",
      timeStyle: "short",
    })
  } catch {
    return d.toISOString().slice(0, 10)
  }
}
